import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, createVideo, getQuiz, getVideo } from "@/lib/api";
import type { PublicQuiz, QuizCounts, VideoMode, VideoSummary } from "@lib/domain";
import { pushPoll } from "@/lib/devPollLog";
import { toWatchUrl } from "@/lib/url";

/** Poll schedule: responsive at first, then backs off so long jobs cost few requests. */
const POLL_BACKOFF_MS = [1000, 2000, 4000, 8000];
const POLL_MAX_INTERVAL_MS = 10000;
/** A restart's POST response still carries the previous status, so ignore `failed` briefly. */
const RESTART_GRACE_POLLS = 2;

function nextPollDelay(polls: number): number {
  const step = POLL_BACKOFF_MS[Math.min(polls, POLL_BACKOFF_MS.length - 1)]!;
  return Math.min(step, POLL_MAX_INTERVAL_MS);
}

export interface PollSample {
  at: number;
  latencyMs: number;
  status: string;
}

export interface VideoJobTelemetry {
  startedAtMs: number | null;
  polls: PollSample[];
}

export interface VideoJob {
  summary: VideoSummary | null;
  quiz: PublicQuiz | null;
  error: string | null;
  loading: boolean;
  /** Re-read the summary without re-running the pipeline. */
  refresh: () => void;
  /** Re-run the pipeline for a failed or partial video and poll it again. */
  retry: () => void;
  /** Client-observed poll timeline + latencies, used by the dev debug panel. */
  telemetry: VideoJobTelemetry;
}

export function useVideoJob(
  videoId: string,
  mode: VideoMode,
  questionCounts?: QuizCounts,
): VideoJob {
  const [summary, setSummary] = useState<VideoSummary | null>(null);
  const [quiz, setQuiz] = useState<PublicQuiz | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [run, setRun] = useState({ restart: false, n: 0 });
  const [telemetry, setTelemetry] = useState<VideoJobTelemetry>({
    startedAtMs: null,
    polls: [],
  });
  const failed = useRef(false);

  const recordSample = useCallback((status: string, latencyMs: number) => {
    pushPoll({ videoId, at: Date.now(), latencyMs, status });
    setTelemetry((t) => ({
      ...t,
      polls: [...t.polls, { at: Date.now(), latencyMs, status }].slice(-200),
    }));
  }, [videoId]);

  const ensure = useCallback(async (): Promise<VideoSummary> => {
    try {
      const existing = await getVideo(videoId);
      failed.current = false;
      return existing;
    } catch (e) {
      if (e instanceof ApiError && e.code === "NOT_FOUND") {
        // Self-heal: the job wasn't created yet (e.g. shared deep link).
        const created = await createVideo(toWatchUrl(videoId), mode, questionCounts);
        failed.current = false;
        return created;
      }
      throw e;
    }
  }, [videoId, mode, questionCounts]);

  /**
   * Re-runs the pipeline. The server enqueues fresh work for a failed or
   * partial video; a complete video is returned untouched.
   */
  const restart = useCallback(async (): Promise<VideoSummary> => {
    const restarted = await createVideo(toWatchUrl(videoId), mode, questionCounts);
    failed.current = false;
    return restarted;
  }, [videoId, mode, questionCounts]);

  const reload = useCallback(async (): Promise<VideoSummary> => {
    const s = await getVideo(videoId);
    failed.current = false;
    return s;
  }, [videoId]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let gracePolls = run.restart ? RESTART_GRACE_POLLS : 0;
    let polls = 0;
    setLoading(true);
    setError(null);
    setTelemetry({ startedAtMs: Date.now(), polls: [] });

    const poll = async () => {
      try {
        const firstStart = performance.now();
        let s = run.restart ? await restart() : await ensure();
        if (cancelled) return;
        recordSample(s.status, Math.round(performance.now() - firstStart));
        setSummary(s);
        while (!cancelled && !failed.current) {
          if (s.status === "ready") {
            setLoading(false);
            return;
          }
          if (s.status === "failed") {
            if (gracePolls > 0) {
              gracePolls -= 1;
            } else {
              setError(s.error ?? "Something went wrong while building your quiz.");
              setLoading(false);
              failed.current = true;
              return;
            }
          }
          await new Promise((resolve) => (timer = setTimeout(resolve, nextPollDelay(polls++))));
          if (cancelled) return;
          const reloadStart = performance.now();
          s = await reload();
          if (cancelled) return;
          recordSample(s.status, Math.round(performance.now() - reloadStart));
          setSummary(s);
        }
      } catch (e) {
        if (!cancelled) {
          recordSample("error", 0);
          setError(e instanceof ApiError ? e.message : "Could not reach the server.");
          setLoading(false);
          failed.current = true;
        }
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [ensure, reload, restart, recordSample, run]);

  useEffect(() => {
    if (!summary || summary.status !== "ready") return;
    let cancelled = false;
    getQuiz(videoId)
      .then((q) => {
        if (!cancelled) setQuiz(q);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : "Could not load the quiz.");
      });
    return () => {
      cancelled = true;
    };
  }, [summary, videoId]);

  const refresh = useCallback(() => {
    setError(null);
    setLoading(true);
    setRun((r) => ({ restart: false, n: r.n + 1 }));
  }, []);

  const retry = useCallback(() => {
    setQuiz(null);
    setError(null);
    setLoading(true);
    setRun((r) => ({ restart: true, n: r.n + 1 }));
  }, []);

  return { summary, quiz, error, loading, refresh, retry, telemetry };
}